import ApiService from '../../services/apiService';
import { mapMutations, mapState, mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import { themesMixin } from '../../utils/themes';
import drawerLinksMixin from './drawerLinks';
export default {
  name: 'CoreDrawer',
  mixins: [themesMixin, drawerLinksMixin],
  data() {
    return {
      cleanedLinks: [],
      userHasKeys: false,
      sidebarLogo: '',
      sidebarLogoOrientation: '',
      sidebarLogoByline: ''
    };
  },
  computed: {
    ...mapState('app', ['image', 'color', 'UiTheme', 'miniVariant', 'themes', 'sidebarIconLink']),
    ...mapGetters({
      sessionUserId: 'authentication/userId'
    }),
    inputValue: {
      get() {
        return this.$store.state.app.drawer;
      },
      set(val) {
        this.setDrawer(val);
      }
    },
    positionTop() {
      return [this.$store.state.app.bannerHeight ? {
        top: `${this.$store.state.app.bannerHeight}px`
      } : {
        top: '0px'
      }];
    },
    sidebarOverlayGradiant() {
      if (this.$store.state.app.UiTheme) {
        // D-SUITE return 'to top left, rgba(6, 20, 3, 1.0), rgba(6, 20, 3, 1.0)';
        return 'to top left, rgba(60, 60, 60, 0.9), rgba(0, 0, 0, 0.9)';
      }
      return 'to top left, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 1.0)';
    },
    responsive() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    drawerLinksWithPermissions() {
      let drawerLinks = cloneDeep(this.links);
      // if back-end side drawer links are available from the theming, use those instead
      if ((this.$store.state.app.drawerLinks || []).length) {
        drawerLinks = cloneDeep(this.$store.state.app.drawerLinks);
      }
      const replacePlaceholders = links => {
        links.forEach(link => {
          if (link.to === 'GRAFANA_PLACEHOLDER') {
            link.to = this.$store.state.app.grafanaUrl;
          }
          if (link.to === 'FIREFLY_PLACEHOLDER') {
            link.to = this.$store.state.app.fireflyUrl;
          }
          if (link.children) {
            replacePlaceholders(link.children);
          }
        });
      };
      replacePlaceholders(drawerLinks);

      // pull the route-permissions from the vue router for each drawer link
      const addPermissions = links => {
        links.forEach(link => {
          if (link.to) {
            const routerPath = this.$router.options.routes.find(route => {
              return route.path === link.to;
            });
            // this is a "local" link, but no router-path was found
            // this probably means the flag was filtered out via excludeEnv
            if (!routerPath && link.to.startsWith('/')) {
              link.permissions = 'NOT_AVAILABLE';
            } else if (!link.permissions) {
              link.permissions = get(routerPath, 'meta.permissions', link.permissions);
            }
          }
          if (link.children) {
            addPermissions(link.children);
          }
        });
      };
      addPermissions(drawerLinks);
      return drawerLinks;
    }
  },
  watch: {
    UiTheme() {
      this.lookupSidebarLogo();
    }
  },
  mounted() {
    this.loadUserKeys();
    this.checkDrawerPermissions();
    this.lookupSidebarLogo();
  },
  methods: {
    ...mapMutations('app', ['setDrawer', 'toggleDrawer']),
    lookupSidebarLogo() {
      const mode = this.UiTheme ? 'dark' : 'light';
      this.sidebarLogo = this.themes[mode].logo;
      this.sidebarLogoOrientation = this.themes[mode].orientation;
      if (this.themes[mode].orientation !== 'portrait' && this.themes[mode].orientation !== 'square') {
        this.sidebarLogoOrientation = 'landscape';
      }
      this.sidebarLogoByline = this.themes[mode].byline;
    },
    async checkDrawerPermissions() {
      this.cleanedLinks = this.filterLinksByPermissions(this.drawerLinksWithPermissions, this.$store.state.app.permissions);
      this.cleanedLinks = this.filterLinksByFeatureFlags(this.cleanedLinks);

      // after filtering by permission get rid of categories that have no links
      this.cleanedLinks = this.cleanedLinks.filter((link, idx, array) => {
        return !(link.category && (array[idx + 1] || {}).category);
      });

      // trigger a UI-refresh
      this.cleanedLinks = cloneDeep(this.cleanedLinks);
    },
    filterLinksByFeatureFlags(links) {
      return cloneDeep(links).filter(link => {
        if (link.children) {
          link.children = this.filterLinksByFeatureFlags(link.children);
        }
        if (link.featureflag) {
          if (link.featureflag.startsWith('!')) {
            return !this.$store.state.app.featureFlags[link.featureflag.slice(1)];
          }
          return this.$store.state.app.featureFlags[link.featureflag];
        }
        return true;
      });
    },
    filterLinksByPermissions(links, userPermissions) {
      return cloneDeep(links).filter(link => {
        if (link.children) {
          link.children = this.filterLinksByPermissions(link.children, userPermissions);

          // no need to display empty groups
          if (get(link, 'children', []).length === 0 && !link.to) {
            return false;
          }
        }
        let preconditions = true;
        link.active = false;
        if (link.conditions) {
          preconditions = Object.keys(link.conditions).every(key => {
            return this[key] === link.conditions[key];
          });
        }
        if (link.permissions) {
          const checkPermissions = permissionSet => {
            if (permissionSet === 'NOT_AVAILABLE') {
              return false;
            }

            // {
            //   or: [
            //     {and: [
            //       { subject: 'DigitalTwin', action: 'manage' },
            //       { subject: 'AutoID', action: 'read', inverted: true }
            //     ]},
            //     { subject: 'all', action: 'manage' }
            //   ]
            // }

            if (permissionSet.or && Array.isArray(permissionSet.or)) {
              return permissionSet.or.find(permission => checkPermissions(permission));
            } else if (permissionSet.and && Array.isArray(permissionSet.and)) {
              return permissionSet.and.every(permission => checkPermissions(permission));
            }
            const hasPermission = userPermissions[`${permissionSet.subject}.${permissionSet.action}`];
            return hasPermission === !permissionSet.inverted;
          };
          return preconditions && checkPermissions(link.permissions);
        }
        return preconditions;
      });
    },
    // UTILS
    // helper methods that retrieve additional data for custom conditions

    loadUserKeys() {
      const canUseKeys = this.$store.state.app.permissions['IsFmAccess.write'] || this.$store.state.app.permissions['IsFmAccess.manage'] || this.$store.state.app.permissions['all.manage'];
      if (canUseKeys) {
        ApiService.isFmAccessFetchByUserId(this.sessionUserId).then(data => {
          this.userHasKeys = data.length > 0;
        }).catch(() => {
          this.userHasKeys = false;
        }).finally(() => {
          this.checkDrawerPermissions();
        });
      } else {
        this.userHasKeys = false;
        this.checkDrawerPermissions();
      }
    }
  }
};